import {
  logAmplitudeEventWithProps,
  createAmplitudeEventProperties,
} from "./amplitude";
import { UserProperties, EventProperties } from "./eventTypes";

/**
 * Event timer class for tracking event durations
 */
export class EventTimer {
  private startTime: number;
  private eventName: string;
  private userAndCompany: string;
  private userProperties: UserProperties;
  private title: string;
  private componentName: string;
  private version: string;
  private completed: boolean = false;

  constructor(
    eventName: string,
    userAndCompany: string,
    userProperties: UserProperties,
    title: string,
    componentName: string,
    version: string
  ) {
    this.eventName = eventName;
    this.userAndCompany = userAndCompany;
    this.userProperties = userProperties;
    this.title = title;
    this.componentName = componentName;
    this.version = version;
    this.startTime = Date.now();
  }

  /**
   * Complete the timer and log to amplitude
   */
  complete(): void {
    if (this.completed) {
      console.warn(`Timer for ${this.eventName} already completed`);
      return;
    }

    this.completed = true;
    const elapsedTime = this.getElapsedTime();

    const eventProperties = createAmplitudeEventProperties(
      this.title,
      this.componentName,
      this.version,
      elapsedTime.toString()
    );

    logAmplitudeEventWithProps(
      this.userAndCompany,
      this.userProperties,
      this.eventName,
      eventProperties
    );
  }

  /**
   * Get elapsed time in milliseconds
   */
  getElapsedTime(): number {
    return Date.now() - this.startTime;
  }
}

/**
 * Create a new event timer
 */
export const createEventTimer = (
  eventName: string,
  userAndCompany: string,
  userProperties: UserProperties,
  title: string,
  componentName: string,
  version: string
): EventTimer => {
  return new EventTimer(
    eventName,
    userAndCompany,
    userProperties,
    title,
    componentName,
    version
  );
};

/**
 * Wrap an async function with event timing
 */
export const withEventTiming = async <T>(
  eventName: string,
  userAndCompany: string,
  userProperties: UserProperties,
  title: string,
  componentName: string,
  version: string,
  fn: () => Promise<T>
): Promise<T> => {
  const timer = createEventTimer(
    eventName,
    userAndCompany,
    userProperties,
    title,
    componentName,
    version
  );

  try {
    const result = await fn();
    timer.complete();
    return result;
  } catch (error) {
    timer.complete();
    throw error;
  }
};

/**
 * Wrap a synchronous function with event timing
 */
export const withSyncEventTiming = <T>(
  eventName: string,
  userAndCompany: string,
  userProperties: UserProperties,
  title: string,
  componentName: string,
  version: string,
  fn: () => T
): T => {
  const timer = createEventTimer(
    eventName,
    userAndCompany,
    userProperties,
    title,
    componentName,
    version
  );

  try {
    const result = fn();
    timer.complete();
    return result;
  } catch (error) {
    timer.complete();
    throw error;
  }
};
